import type { BeforeSendHookPayload } from 'mixpanel-browser';

const OMITTED_PROPERTIES = ['current_url_search', 'current_page_title'];

function sanitizePath(pathname: string): string {
  const segments = decodeURIComponent(pathname).split('/');
  const sensitiveIndex = segments.findIndex((segment) =>
    ['token-login', 'email-confirmation', 'partner-signup'].includes(segment.toLowerCase()),
  );
  if (sensitiveIndex === -1) {
    return pathname;
  }
  const prefix = segments.slice(0, sensitiveIndex + 1).join('/');
  return segments[sensitiveIndex].toLowerCase() === 'partner-signup' ? prefix : `${prefix}/:token`;
}

export function sanitizeMixpanelUrl(value: unknown): string {
  if (typeof value !== 'string' || (!value.startsWith('/') && !/^https?:\/\//i.test(value))) {
    return '';
  }
  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '';
    }
    const sanitizedUrl = new URL(url.origin);
    sanitizedUrl.pathname = sanitizePath(url.pathname);
    return sanitizedUrl.href;
  } catch {
    return '';
  }
}

export function sanitizeMixpanelProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...properties };
  for (const key of OMITTED_PROPERTIES) {
    delete sanitized[key];
  }
  if ('$current_url' in sanitized) {
    sanitized.$current_url = sanitizeMixpanelUrl(sanitized.$current_url);
  }
  if ('current_url_path' in sanitized) {
    const url = sanitizeMixpanelUrl(sanitized.current_url_path);
    sanitized.current_url_path = url ? new URL(url).pathname : '';
  }
  for (const key of ['$referrer', '$initial_referrer']) {
    if (key in sanitized && sanitized[key] !== '$direct') {
      sanitized[key] = sanitizeMixpanelUrl(sanitized[key]);
    }
  }
  for (const key of ['$referring_domain', '$initial_referring_domain']) {
    const value = sanitized[key];
    if (key in sanitized && value !== '$direct') {
      const url =
        typeof value === 'string' && value.length > 0
          ? sanitizeMixpanelUrl(value.includes('://') ? value : `https://${value}`)
          : '';
      sanitized[key] = url ? new URL(url).host : '';
    }
  }
  return sanitized;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function sanitizePeoplePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const sanitized = { ...payload };
  for (const key of ['$set', '$set_once']) {
    const properties = sanitized[key];
    if (isRecord(properties)) {
      sanitized[key] = sanitizeMixpanelProperties(properties);
    }
  }
  return sanitized;
}

export function createMixpanelPrivacyHooks() {
  return {
    on_track: (name: string, properties: Record<string, unknown>) => [
      name,
      sanitizeMixpanelProperties(properties),
    ],
    before_send_events: (payload: BeforeSendHookPayload): BeforeSendHookPayload => ({
      ...payload,
      properties: sanitizeMixpanelProperties(payload.properties),
    }),
    // SDK 2.84 supports this hook in its shared request pipeline; its typings omit it.
    before_send_people: sanitizePeoplePayload,
  };
}
